import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Coins, Send, Loader2, CheckCircle2 } from "lucide-react";

export default function CoinSellerApply() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: user?.username ? `@${user.username}` : "",
    email: user?.email || "",
    agent_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.first_name.trim() || !form.last_name.trim() || !form.agent_name.trim() || !form.email.trim()) {
      setErr("Please fill all required fields");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setErr("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      await api.post("/applications/agent", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
        agent_name: form.agent_name.trim(),
      });
      setOk(true);
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  if (ok) {
    return (
      <div className="px-4 py-10 max-w-lg mx-auto text-center" data-testid="agent-success">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400"/>
        </div>
        <h1 className="font-outfit font-extrabold text-2xl mb-2">Application received 💰</h1>
        <p className="text-white/70 text-sm mb-6">Thank you for applying to become a Coin-Seller Agent for Hifireels. Our team will review your application and reach out on <span className="text-[#00E5FF]">{form.email}</span> within 2-3 business days with onboarding instructions.</p>
        <button onClick={() => nav("/more")} data-testid="agent-back-home"
          className="px-6 py-3 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#A855F7] text-white font-outfit font-extrabold active:scale-95">
          Back to Discover
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 pb-24" data-testid="agent-form-page">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav(-1)} data-testid="agent-back" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div>
          <h1 className="font-outfit font-extrabold text-2xl leading-tight">Become Coin Seller / Agent</h1>
          <p className="text-white/60 text-xs">Offline recharge & Goldly reseller programme</p>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 to-[#00E5FF]/10 border border-[#FF7A00]/25 mb-5 flex items-start gap-3">
        <Coins className="w-6 h-6 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-outfit font-bold text-sm mb-0.5">Offline Recharge Programme</div>
          <p className="text-[11px] text-white/70 leading-relaxed">Sell Goldly recharges offline in your area at your own margin. Ideal for shopkeepers, mobile-recharge outlets, cyber-cafes, and local business owners. Earn commission on every recharge.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="agent-form">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">First name *</label>
            <input value={form.first_name} onChange={upd("first_name")} required maxLength={60}
              placeholder="Your first name" data-testid="agent-first-name"
              className="mt-1 w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Last name *</label>
            <input value={form.last_name} onChange={upd("last_name")} required maxLength={60}
              placeholder="Your last name" data-testid="agent-last-name"
              className="mt-1 w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">User name / ID</label>
          <input value={form.username} onChange={upd("username")} maxLength={60}
            placeholder="@yourhandle" data-testid="agent-username"
            className="mt-1 w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Email ID *</label>
          <input type="email" value={form.email} onChange={upd("email")} required maxLength={120}
            placeholder="you@example.com" data-testid="agent-email"
            className="mt-1 w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Agent name *</label>
          <input value={form.agent_name} onChange={upd("agent_name")} required maxLength={120}
            placeholder="Your shop / business / agent name" data-testid="agent-name"
            className="mt-1 w-full px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
        </div>

        {err && <div className="text-[#FF2A5F] text-xs" data-testid="agent-err">{err}</div>}

        <button disabled={busy} data-testid="agent-submit"
          className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#00E5FF] text-black font-outfit font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          Submit Application
        </button>
        <p className="text-[10px] text-white/40 text-center mt-1">Your application will be sent to the Hifireels team. We&apos;ll get back to you on the email you provided.</p>
      </form>
    </div>
  );
}
